<template>
  <AcademyLayout @enroll="enrollOpen = true">
    <!-- 列表页：慕课风格英雄区 + 卡片栅格 -->
    <template v-if="phase === 'pick'">
      <section class="ex-hero">
        <div class="ex-hero__bg" aria-hidden="true"></div>
        <div class="ac-wrap ex-hero__inner">
          <p class="ex-hero__kicker">高仁学堂 · Online Assessment</p>
          <h1>在线考试</h1>
          <p class="ex-hero__lead">
            为每门培训课程配备独立结业测评。选择课程后即可开始答题，完成后查看分数与解析。
          </p>
          <ul class="ex-hero__facts">
            <li><strong>{{ examCards.length }}</strong><span>门课程考试</span></li>
            <li><strong>即时</strong><span>出分与解析</span></li>
            <li><strong>60</strong><span>分及格标准</span></li>
          </ul>
        </div>
      </section>

      <div class="ac-wrap ex-page">
        <div class="ex-toolbar">
          <h2>全部考试</h2>
          <span>共 {{ examCards.length }} 场 · 单选题 · 限时作答</span>
        </div>

        <div class="ex-grid">
          <article
            v-for="(item, idx) in examCards"
            :key="item.course.id"
            class="ex-card"
            :style="{ '--delay': `${idx * 40}ms` }"
            @click="selectCourse(item.course.id)"
          >
            <div class="ex-card__cover" :class="`ex-card__cover--${item.course.coverTone}`">
              <span class="ex-card__dur">{{ item.course.duration }}</span>
              <strong>{{ item.course.shortTitle || item.course.category }}</strong>
              <em>{{ item.course.category }}</em>
            </div>
            <div class="ex-card__body">
              <h3>{{ item.course.title }}</h3>
              <p>{{ item.meta.description }}</p>
              <div class="ex-card__chips">
                <span>{{ item.questions.length }} 题</span>
                <span>{{ item.meta.durationMin }} 分钟</span>
                <span>满分 {{ item.meta.totalScore }}</span>
              </div>
              <div class="ex-card__foot">
                <span class="ex-card__learners">{{ item.course.learners }} 人已学</span>
                <span class="ex-card__cta">进入考试</span>
              </div>
            </div>
          </article>
        </div>
      </div>
    </template>

    <div v-else class="ac-wrap ex-page">
      <!-- 考试说明：一体式主卡 + 侧栏参数 -->
      <section v-if="phase === 'intro' && activeExam" class="ex-intro">
        <button type="button" class="ex-linkback" @click="backToPick">
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" d="M15 6l-6 6 6 6" />
          </svg>
          返回考试列表
        </button>

        <div class="ex-intro__grid">
          <article class="ex-panel">
            <header class="ex-panel__hero" :class="`ex-panel__hero--${activeExam.course.coverTone}`">
              <div class="ex-panel__hero-top">
                <span class="ex-panel__badge">{{ activeExam.course.category }}</span>
                <span class="ex-panel__badge ex-panel__badge--ghost">结业测评</span>
              </div>
              <h1>{{ activeExam.meta.title }}</h1>
              <p>{{ activeExam.course.title }}</p>
            </header>

            <div class="ex-panel__body">
              <div class="ex-panel__section">
                <h2>考试说明</h2>
                <p class="ex-panel__desc">{{ activeExam.meta.description }}</p>
              </div>

              <div class="ex-rules">
                <div class="ex-rules__item">
                  <span>01</span>
                  <div>
                    <strong>题型与出分</strong>
                    <p>全部为单选题，提交后立即显示成绩与解析</p>
                  </div>
                </div>
                <div class="ex-rules__item">
                  <span>02</span>
                  <div>
                    <strong>限时作答</strong>
                    <p>倒计时结束将自动交卷，请合理安排答题时间</p>
                  </div>
                </div>
                <div class="ex-rules__item">
                  <span>03</span>
                  <div>
                    <strong>答题环境</strong>
                    <p>答题过程中请勿刷新或关闭页面，以免进度丢失</p>
                  </div>
                </div>
                <div class="ex-rules__item">
                  <span>04</span>
                  <div>
                    <strong>备考建议</strong>
                    <p>建议先复习课程章节，再进行结业测评</p>
                  </div>
                </div>
              </div>
            </div>
          </article>

          <aside class="ex-checkout">
            <div class="ex-checkout__card">
              <div class="ex-checkout__head">
                <strong>{{ activeExam.course.shortTitle }}</strong>
                <span>结业测评</span>
              </div>

              <div class="ex-checkout__stats">
                <div>
                  <em>{{ activeExam.questions.length }}</em>
                  <span>题目数量</span>
                </div>
                <div>
                  <em>{{ activeExam.meta.durationMin }}</em>
                  <span>考试时长(分)</span>
                </div>
                <div>
                  <em>{{ activeExam.meta.passScore }}</em>
                  <span>及格分数</span>
                </div>
                <div>
                  <em>{{ activeExam.meta.totalScore }}</em>
                  <span>试卷满分</span>
                </div>
              </div>

              <ul class="ex-checkout__notes">
                <li>单选题 · 自动判分</li>
                <li>开始后计时不可暂停</li>
              </ul>

              <button type="button" class="ex-primary" @click="startExam">开始考试</button>
            </div>
          </aside>
        </div>
      </section>

      <!-- 答题中 -->
      <section v-else-if="phase === 'doing' && activeExam" class="ex-doing">
        <div class="ex-doing__progress">
          <div class="ex-doing__bar" :style="{ width: `${progressPct}%` }"></div>
        </div>

        <div class="ex-doing__grid">
          <aside class="ex-sheet-panel">
            <div class="ex-timer" :class="{ 'is-low': remainSec <= 60 }">
              <span>剩余时间</span>
              <strong>{{ remainText }}</strong>
            </div>
            <div class="ex-sheet-panel__head">
              <h3>答题卡</h3>
              <span>{{ answeredCount }}/{{ activeExam.questions.length }}</span>
            </div>
            <div class="ex-sheet">
              <button
                v-for="(q, idx) in activeExam.questions"
                :key="q.id"
                type="button"
                class="ex-sheet__dot"
                :class="{
                  'is-cur': idx === currentIndex,
                  'is-done': answers[q.id] !== undefined && answers[q.id] !== null,
                }"
                @click="currentIndex = idx"
              >
                {{ idx + 1 }}
              </button>
            </div>
            <p class="ex-sheet-panel__course">{{ activeExam.course.shortTitle }}</p>
          </aside>

          <div class="ex-q-panel">
            <header class="ex-q-panel__head">
              <span class="ex-tag">单选题</span>
              <span class="ex-tag ex-tag--soft">{{ current?.score }} 分</span>
              <em>第 {{ currentIndex + 1 }} / {{ activeExam.questions.length }} 题</em>
            </header>

            <article v-if="current" class="ex-q">
              <h2>{{ current.stem }}</h2>
              <label
                v-for="(opt, oi) in current.options"
                :key="oi"
                class="ex-opt"
                :class="{ 'is-on': answers[current.id] === oi }"
              >
                <input type="radio" :name="current.id" :value="oi" v-model.number="answers[current.id]" />
                <i>{{ String.fromCharCode(65 + oi) }}</i>
                <span>{{ opt }}</span>
              </label>
            </article>

            <footer class="ex-q-panel__foot">
              <button type="button" class="ex-ghost" :disabled="currentIndex === 0" @click="currentIndex--">
                上一题
              </button>
              <div class="ex-q-panel__right">
                <button
                  v-if="currentIndex < activeExam.questions.length - 1"
                  type="button"
                  class="ex-secondary"
                  @click="currentIndex++"
                >
                  下一题
                </button>
                <button type="button" class="ex-primary" @click="submitExam">交卷</button>
              </div>
            </footer>
          </div>
        </div>
      </section>

      <!-- 结果 -->
      <section v-else-if="phase === 'result' && activeExam" class="ex-result-page">
        <div class="ex-score" :class="passed ? 'is-pass' : 'is-fail'">
          <p>{{ activeExam.course.shortTitle }} · 考试结果</p>
          <div class="ex-score__ring">
            <strong>{{ score }}</strong>
            <span>分</span>
          </div>
          <h2>{{ passed ? '恭喜，测评已通过' : '未通过，建议复习后重考' }}</h2>
          <p class="ex-score__meta">
            满分 {{ activeExam.meta.totalScore }} · 及格 {{ activeExam.meta.passScore }} ·
            正确 {{ correctCount }} / {{ activeExam.questions.length }}
          </p>
          <div class="ex-score__actions">
            <button type="button" class="ex-primary" @click="restart">再考一次</button>
            <button type="button" class="ex-ghost" @click="backToPick">选择其他课程</button>
            <router-link :to="{ name: 'training' }" class="ex-ghost">返回学堂首页</router-link>
          </div>
        </div>

        <div class="ex-review">
          <header class="ex-review__head">
            <h3>题目解析</h3>
            <span>逐题对照正确答案</span>
          </header>
          <article
            v-for="(q, idx) in activeExam.questions"
            :key="q.id"
            class="ex-review__item"
            :class="answers[q.id] === q.answer ? 'is-ok' : 'is-bad'"
          >
            <div class="ex-review__top">
              <span class="ex-review__idx">{{ idx + 1 }}</span>
              <h4>{{ q.stem }}</h4>
              <em>{{ answers[q.id] === q.answer ? '正确' : '错误' }}</em>
            </div>
            <p>你的答案：{{ optionLabel(answers[q.id]) }} · 正确答案：{{ optionLabel(q.answer) }}</p>
            <p class="ex-review__explain">{{ q.explain }}</p>
          </article>
        </div>
      </section>
    </div>

    <AcademyEnrollModal v-model="enrollOpen" :course="null" />
  </AcademyLayout>
</template>

<script setup>
import { computed, onUnmounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import AcademyLayout from '../components/AcademyLayout.vue'
import AcademyEnrollModal from '../components/AcademyEnrollModal.vue'
import { getCourseExam, listExamCourses } from '../data/trainingCatalog'

const route = useRoute()
const router = useRouter()
const enrollOpen = ref(false)
const phase = ref('pick')
const selectedCourseId = ref('')
const currentIndex = ref(0)
const answers = reactive({})
const score = ref(0)
const remainSec = ref(0)
let timer = null

const examCards = computed(() =>
  listExamCourses()
    .map((course) => getCourseExam(course.id))
    .filter(Boolean)
)

const activeExam = computed(() =>
  selectedCourseId.value ? getCourseExam(selectedCourseId.value) : null
)

const current = computed(() => activeExam.value?.questions?.[currentIndex.value] || null)
const passed = computed(() =>
  activeExam.value ? score.value >= activeExam.value.meta.passScore : false
)
const answeredCount = computed(() => {
  const qs = activeExam.value?.questions || []
  return qs.filter((q) => answers[q.id] !== undefined && answers[q.id] !== null).length
})
const correctCount = computed(() => {
  const qs = activeExam.value?.questions || []
  return qs.filter((q) => answers[q.id] === q.answer).length
})
const progressPct = computed(() => {
  const total = activeExam.value?.questions?.length || 1
  return Math.round(((currentIndex.value + 1) / total) * 100)
})
const remainText = computed(() => {
  const m = Math.floor(remainSec.value / 60)
  const s = remainSec.value % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
})

watch(
  () => route.query.course,
  (id) => {
    if (id && getCourseExam(id)) {
      selectCourse(String(id), false)
    }
  },
  { immediate: true }
)

function optionLabel(idx) {
  if (idx === undefined || idx === null) return '未作答'
  return String.fromCharCode(65 + idx)
}

function clearTimer() {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}

function selectCourse(courseId, syncQuery = true) {
  if (!getCourseExam(courseId)) return
  clearTimer()
  selectedCourseId.value = courseId
  Object.keys(answers).forEach((k) => delete answers[k])
  currentIndex.value = 0
  score.value = 0
  phase.value = 'intro'
  if (syncQuery && route.query.course !== courseId) {
    router.replace({ query: { ...route.query, course: courseId } })
  }
}

function backToPick() {
  clearTimer()
  phase.value = 'pick'
  selectedCourseId.value = ''
  const query = { ...route.query }
  delete query.course
  router.replace({ query })
}

function startExam() {
  if (!activeExam.value) return
  Object.keys(answers).forEach((k) => delete answers[k])
  currentIndex.value = 0
  remainSec.value = activeExam.value.meta.durationMin * 60
  score.value = 0
  phase.value = 'doing'
  clearTimer()
  timer = window.setInterval(() => {
    remainSec.value -= 1
    if (remainSec.value <= 0) {
      clearTimer()
      ElMessage.warning('考试时间到，已自动交卷')
      submitExam()
    }
  }, 1000)
}

function submitExam() {
  if (!activeExam.value) return
  clearTimer()
  let total = 0
  activeExam.value.questions.forEach((q) => {
    if (answers[q.id] === q.answer) total += q.score
  })
  score.value = total
  phase.value = 'result'
}

function restart() {
  phase.value = 'intro'
  clearTimer()
}

onUnmounted(clearTimer)
</script>

<style scoped>
.ac-wrap {
  width: min(1160px, calc(100% - 40px));
  margin: 0 auto;
}

/* ===== Hero ===== */
.ex-hero {
  position: relative;
  margin-bottom: 8px;
  overflow: hidden;
  color: #fff;
  background: #0f2744;
}

.ex-hero__bg {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 80% 70% at 12% 20%, rgba(45, 140, 240, 0.45), transparent 55%),
    radial-gradient(ellipse 60% 50% at 88% 10%, rgba(255, 106, 0, 0.28), transparent 50%),
    linear-gradient(135deg, #0c223a 0%, #143356 48%, #0f2744 100%);
}

.ex-hero__bg::after {
  content: '';
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: linear-gradient(180deg, #000 30%, transparent);
  opacity: 0.5;
}

.ex-hero__inner {
  position: relative;
  z-index: 1;
  padding: 48px 0 44px;
}

.ex-hero__kicker {
  margin: 0 0 12px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
}

.ex-hero h1 {
  margin: 0 0 12px;
  font-size: clamp(32px, 4vw, 44px);
  font-weight: 800;
  letter-spacing: 0.02em;
  line-height: 1.15;
}

.ex-hero__lead {
  margin: 0;
  max-width: 560px;
  font-size: 15px;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.78);
}

.ex-hero__facts {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 28px;
  list-style: none;
  margin: 28px 0 0;
  padding: 0;
}

.ex-hero__facts li {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.ex-hero__facts strong {
  font-size: 28px;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.02em;
}

.ex-hero__facts span {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.65);
}

/* ===== List ===== */
.ex-page {
  padding: 28px 0 16px;
}

.ex-toolbar {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}

.ex-toolbar h2 {
  margin: 0;
  font-size: 22px;
  color: #152033;
}

.ex-toolbar span {
  font-size: 13px;
  color: #98a2b3;
}

.ex-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.ex-card {
  display: flex;
  flex-direction: column;
  border: 1px solid #e8eef5;
  border-radius: 16px;
  background: #fff;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 10px 28px rgba(20, 40, 70, 0.05);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
  animation: ex-rise 0.45s ease both;
  animation-delay: var(--delay, 0ms);
}

.ex-card:hover {
  transform: translateY(-4px);
  border-color: rgba(45, 140, 240, 0.35);
  box-shadow: 0 18px 40px rgba(30, 70, 120, 0.12);
}

@keyframes ex-rise {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.ex-card__cover {
  position: relative;
  min-height: 128px;
  padding: 18px 18px 16px;
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 6px;
}

.ex-card__cover--1 { background: linear-gradient(145deg, #1f7fe0, #4aa3f5); }
.ex-card__cover--2 { background: linear-gradient(145deg, #12a35a, #3dce7d); }
.ex-card__cover--3 { background: linear-gradient(145deg, #f06200, #ff8f3d); }
.ex-card__cover--4 { background: linear-gradient(145deg, #1a4f8c, #0f1f35); }

.ex-card__cover::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 90% 10%, rgba(255, 255, 255, 0.22), transparent 40%),
    radial-gradient(circle at 10% 90%, rgba(0, 0, 0, 0.12), transparent 45%);
  pointer-events: none;
}

.ex-card__dur {
  position: relative;
  align-self: flex-start;
  padding: 3px 9px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.22);
  font-size: 11px;
  font-weight: 700;
  margin-bottom: auto;
}

.ex-card__cover strong,
.ex-card__cover em {
  position: relative;
}

.ex-card__cover strong {
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.ex-card__cover em {
  font-style: normal;
  font-size: 12px;
  opacity: 0.85;
}

.ex-card__body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 18px 18px;
  flex: 1;
}

.ex-card__body h3 {
  margin: 0;
  font-size: 15px;
  line-height: 1.45;
  color: #152033;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 2.9em;
}

.ex-card__body > p {
  margin: 0;
  font-size: 12px;
  line-height: 1.65;
  color: #98a2b3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
}

.ex-card__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.ex-card__chips span {
  padding: 3px 8px;
  border-radius: 6px;
  background: #f3f6fa;
  color: #5b6573;
  font-size: 11px;
  font-weight: 600;
}

.ex-card__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 10px;
  border-top: 1px solid #eef2f7;
}

.ex-card__learners {
  font-size: 12px;
  color: #98a2b3;
}

.ex-card__cta {
  font-size: 13px;
  font-weight: 700;
  color: #ff6a00;
}

.ex-card:hover .ex-card__cta {
  color: #e85f00;
}

/* ===== Intro ===== */
.ex-intro {
  padding-top: 4px;
}

.ex-linkback {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 16px;
  padding: 0;
  border: none;
  background: none;
  color: #2d8cf0;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.ex-intro__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 20px;
  align-items: start;
}

.ex-panel {
  background: #fff;
  border: 1px solid #e8eef5;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 28px rgba(30, 55, 90, 0.05);
}

.ex-panel__hero {
  position: relative;
  padding: 28px 28px 26px;
  color: #fff;
  overflow: hidden;
}

.ex-panel__hero::after {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 92% 0%, rgba(255, 255, 255, 0.22), transparent 42%),
    radial-gradient(circle at 0% 100%, rgba(0, 0, 0, 0.12), transparent 48%);
  pointer-events: none;
}

.ex-panel__hero--1 { background: linear-gradient(135deg, #1f7fe0, #4aa3f5); }
.ex-panel__hero--2 { background: linear-gradient(135deg, #12a35a, #3dce7d); }
.ex-panel__hero--3 { background: linear-gradient(135deg, #f06200, #ff8f3d); }
.ex-panel__hero--4 { background: linear-gradient(135deg, #1a4f8c, #0f1f35); }

.ex-panel__hero-top,
.ex-panel__hero h1,
.ex-panel__hero > p {
  position: relative;
  z-index: 1;
}

.ex-panel__hero-top {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}

.ex-panel__badge {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.2);
  font-size: 12px;
  font-weight: 700;
}

.ex-panel__badge--ghost {
  background: rgba(255, 255, 255, 0.18);
}

.ex-panel__hero h1 {
  margin: 0 0 8px;
  font-size: clamp(24px, 2.8vw, 30px);
  line-height: 1.3;
  font-weight: 800;
}

.ex-panel__hero > p {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  opacity: 0.92;
}

.ex-panel__body {
  padding: 24px 28px 28px;
}

.ex-panel__section {
  margin-bottom: 18px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eef2f7;
}

.ex-panel__section h2 {
  margin: 0 0 8px;
  font-size: 18px;
  color: #152033;
}

.ex-panel__desc {
  margin: 0;
  font-size: 14px;
  line-height: 1.75;
  color: #667085;
}

.ex-rules {
  display: grid;
  gap: 12px;
}

.ex-rules__item {
  display: grid;
  grid-template-columns: 40px 1fr;
  gap: 12px;
  align-items: start;
  padding: 14px 14px;
  border-radius: 12px;
  background: #f7f9fc;
  border: 1px solid #eef2f7;
}

.ex-rules__item span {
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #e8eef5;
  color: #2d8cf0;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.ex-rules__item strong {
  display: block;
  margin-bottom: 4px;
  font-size: 14px;
  color: #152033;
}

.ex-rules__item p {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #667085;
}

.ex-checkout {
  position: sticky;
  top: 76px;
}

.ex-checkout__card {
  padding: 22px 20px 20px;
  background: #fff;
  border: 1px solid #e8eef5;
  border-radius: 16px;
  box-shadow: 0 14px 36px rgba(30, 55, 90, 0.08);
}

.ex-checkout__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid #eef2f7;
}

.ex-checkout__head strong {
  font-size: 18px;
  color: #152033;
}

.ex-checkout__head span {
  font-size: 12px;
  color: #98a2b3;
  white-space: nowrap;
}

.ex-checkout__stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 14px;
}

.ex-checkout__stats > div {
  padding: 14px 12px;
  border-radius: 12px;
  background: linear-gradient(180deg, #f7faff, #f3f7fc);
  border: 1px solid #e8eef5;
  text-align: center;
}

.ex-checkout__stats em {
  display: block;
  margin-bottom: 4px;
  font-style: normal;
  font-size: 26px;
  font-weight: 800;
  line-height: 1;
  color: #152033;
  letter-spacing: -0.02em;
}

.ex-checkout__stats span {
  font-size: 12px;
  color: #98a2b3;
}

.ex-checkout__notes {
  list-style: none;
  margin: 0 0 16px;
  padding: 0;
}

.ex-checkout__notes li {
  position: relative;
  padding: 6px 0 6px 14px;
  font-size: 12px;
  color: #667085;
  line-height: 1.5;
}

.ex-checkout__notes li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 12px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #ff6a00;
}

.ex-primary,
.ex-secondary,
.ex-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.ex-primary {
  width: 100%;
  border: none;
  background: #ff6a00;
  color: #fff;
  box-shadow: 0 8px 18px rgba(255, 106, 0, 0.25);
}

.ex-primary:hover {
  background: #e85f00;
}

.ex-secondary {
  border: none;
  background: #2d8cf0;
  color: #fff;
}

.ex-secondary:hover {
  background: #1f78d8;
}

.ex-ghost {
  border: 1px solid #d0d5dd;
  background: #fff;
  color: #5b6573;
}

.ex-ghost:hover {
  border-color: #2d8cf0;
  color: #2d8cf0;
}

.ex-ghost:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ===== Doing ===== */
.ex-doing__progress {
  height: 3px;
  margin: 0 0 16px;
  border-radius: 999px;
  background: #e8eef5;
  overflow: hidden;
}

.ex-doing__bar {
  height: 100%;
  background: linear-gradient(90deg, #2d8cf0, #ff6a00);
  transition: width 0.25s ease;
}

.ex-doing__grid {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}

.ex-sheet-panel,
.ex-q-panel {
  background: #fff;
  border: 1px solid #e8eef5;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(30, 55, 90, 0.04);
}

.ex-sheet-panel {
  position: sticky;
  top: 76px;
  padding: 18px;
}

.ex-timer {
  padding: 14px;
  margin-bottom: 16px;
  border-radius: 12px;
  background: rgba(255, 106, 0, 0.08);
  text-align: center;
}

.ex-timer.is-low {
  background: rgba(220, 38, 38, 0.08);
}

.ex-timer span {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  color: #ff6a00;
}

.ex-timer.is-low span,
.ex-timer.is-low strong {
  color: #dc2626;
}

.ex-timer strong {
  font-size: 30px;
  letter-spacing: 0.06em;
  color: #ff6a00;
  font-variant-numeric: tabular-nums;
}

.ex-sheet-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.ex-sheet-panel__head h3 {
  margin: 0;
  font-size: 14px;
  color: #152033;
}

.ex-sheet-panel__head span {
  font-size: 12px;
  color: #98a2b3;
}

.ex-sheet {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
}

.ex-sheet__dot {
  aspect-ratio: 1;
  border-radius: 8px;
  border: 1px solid #e8eef5;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  color: #667085;
}

.ex-sheet__dot.is-done {
  background: rgba(45, 140, 240, 0.12);
  border-color: transparent;
  color: #2d8cf0;
}

.ex-sheet__dot.is-cur {
  background: #2d8cf0;
  border-color: transparent;
  color: #fff;
}

.ex-sheet-panel__course {
  margin: 14px 0 0;
  font-size: 12px;
  color: #98a2b3;
}

.ex-q-panel {
  padding: 24px 26px 20px;
}

.ex-q-panel__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
}

.ex-q-panel__head em {
  margin-left: auto;
  font-style: normal;
  font-size: 13px;
  color: #98a2b3;
}

.ex-tag {
  display: inline-flex;
  padding: 3px 10px;
  border-radius: 999px;
  background: rgba(45, 140, 240, 0.12);
  color: #2d8cf0;
  font-size: 12px;
  font-weight: 700;
}

.ex-tag--soft {
  background: #eef2f7;
  color: #667085;
}

.ex-q h2 {
  margin: 0 0 18px;
  font-size: 20px;
  line-height: 1.55;
  color: #152033;
  font-weight: 700;
}

.ex-opt {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  margin-bottom: 10px;
  border: 1px solid #e8eef5;
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.ex-opt input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.ex-opt i {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #f3f6fa;
  color: #667085;
  font-style: normal;
  font-size: 13px;
  font-weight: 800;
}

.ex-opt span {
  padding-top: 3px;
  font-size: 14px;
  line-height: 1.55;
  color: #152033;
}

.ex-opt:hover {
  border-color: rgba(45, 140, 240, 0.45);
  background: #f7fbff;
}

.ex-opt.is-on {
  border-color: #2d8cf0;
  background: rgba(45, 140, 240, 0.06);
  box-shadow: 0 0 0 3px rgba(45, 140, 240, 0.08);
}

.ex-opt.is-on i {
  background: #2d8cf0;
  color: #fff;
}

.ex-q-panel__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid #eef2f7;
}

.ex-q-panel__right {
  display: flex;
  gap: 10px;
}

.ex-q-panel__right .ex-primary {
  width: auto;
}

/* ===== Result ===== */
.ex-score {
  text-align: center;
  padding: 36px 28px 28px;
  border-radius: 18px;
  margin-bottom: 18px;
  border: 1px solid #e8eef5;
  background: #fff;
  box-shadow: 0 12px 32px rgba(30, 55, 90, 0.06);
}

.ex-score.is-pass {
  background: linear-gradient(180deg, #f0faf5, #fff 55%);
  border-color: #c9ebd8;
}

.ex-score.is-fail {
  background: linear-gradient(180deg, #fff7f0, #fff 55%);
  border-color: #ffd7bf;
}

.ex-score > p:first-child {
  margin: 0 0 12px;
  font-size: 13px;
  font-weight: 700;
  color: #98a2b3;
  letter-spacing: 0.04em;
}

.ex-score__ring {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 8px;
}

.ex-score__ring strong {
  font-size: 64px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: -0.03em;
}

.ex-score.is-pass .ex-score__ring strong { color: #1b8a5a; }
.ex-score.is-fail .ex-score__ring strong { color: #ff6a00; }

.ex-score__ring span {
  font-size: 18px;
  font-weight: 700;
  color: #98a2b3;
}

.ex-score h2 {
  margin: 0 0 8px;
  font-size: 22px;
  color: #152033;
}

.ex-score__meta {
  margin: 0 0 22px;
  font-size: 13px;
  color: #98a2b3;
}

.ex-score__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
}

.ex-score__actions .ex-primary {
  width: auto;
}

.ex-review {
  background: #fff;
  border: 1px solid #e8eef5;
  border-radius: 16px;
  padding: 8px 24px 12px;
  box-shadow: 0 8px 24px rgba(30, 55, 90, 0.04);
}

.ex-review__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 0 12px;
  border-bottom: 1px solid #eef2f7;
}

.ex-review__head h3 {
  margin: 0;
  font-size: 18px;
  color: #152033;
}

.ex-review__head span {
  font-size: 12px;
  color: #98a2b3;
}

.ex-review__item {
  padding: 18px 0;
  border-bottom: 1px solid #eef2f7;
}

.ex-review__item:last-child {
  border-bottom: none;
}

.ex-review__top {
  display: grid;
  grid-template-columns: 28px 1fr auto;
  gap: 10px;
  align-items: start;
  margin-bottom: 8px;
}

.ex-review__idx {
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  background: #f3f6fa;
  font-size: 12px;
  font-weight: 800;
  color: #667085;
}

.ex-review__item.is-ok .ex-review__idx {
  background: rgba(27, 138, 90, 0.12);
  color: #1b8a5a;
}

.ex-review__item.is-bad .ex-review__idx {
  background: rgba(255, 106, 0, 0.12);
  color: #ff6a00;
}

.ex-review__top h4 {
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: #152033;
}

.ex-review__top em {
  font-style: normal;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.ex-review__item.is-ok .ex-review__top em { color: #1b8a5a; }
.ex-review__item.is-bad .ex-review__top em { color: #ff6a00; }

.ex-review__item > p {
  margin: 0 0 4px;
  padding-left: 38px;
  font-size: 13px;
  color: #667085;
}

.ex-review__explain {
  color: #98a2b3 !important;
}

@media (max-width: 1020px) {
  .ex-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .ex-intro__grid {
    grid-template-columns: 1fr;
  }

  .ex-checkout {
    position: static;
  }
}

@media (max-width: 860px) {
  .ex-doing__grid {
    grid-template-columns: 1fr;
  }

  .ex-sheet-panel {
    position: static;
  }
}

@media (max-width: 640px) {
  .ac-wrap {
    width: calc(100% - 24px);
  }

  .ex-hero__inner {
    padding: 36px 0 32px;
  }

  .ex-grid {
    grid-template-columns: 1fr;
  }

  .ex-q-panel {
    padding: 18px 16px;
  }

  .ex-q-panel__foot {
    flex-direction: column;
    align-items: stretch;
  }

  .ex-q-panel__right {
    width: 100%;
  }

  .ex-q-panel__right .ex-primary,
  .ex-q-panel__right .ex-secondary {
    flex: 1;
  }

  .ex-score__actions {
    flex-direction: column;
  }

  .ex-score__actions .ex-primary,
  .ex-score__actions .ex-ghost {
    width: 100%;
  }

  .ex-review {
    padding: 4px 14px 8px;
  }

  .ex-review__item > p {
    padding-left: 0;
  }
}
</style>
